import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixRestaurantsMenusV1Item from './restaurants-menus-v1-item-items.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

export interface Item extends ItemPricingOneOf {
  /** Item price variants. */
  priceVariants?: PriceVariants;
  /** Item price info. */
  priceInfo?: PriceInfo;
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the item was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Item name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  /**
   * Item description.
   * @maxLength 1500
   */
  description?: string | null;
  /** Main item image. */
  image?: string;
  /**
   * Additional item images.
   * @maxSize 15
   */
  additionalImages?: string[];
  /**
   * Item labels.
   * @maxSize 10
   */
  labels?: Label[];
  /** Whether the item is visible in the menu for site visitors. */
  visible?: boolean | null;
  /**
   * Whether the item is marked as featured/best seller.
   * Default: `false`.
   */
  featured?: boolean | null;
  /** Online order settings. */
  orderSettings?: OrderSettings;
  /**
   * Item modifier groups.
   * @maxSize 25
   */
  modifierGroups?: ModifierGroup[];
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * IDs of the business locations ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where these items are available.
   * @maxSize 100
   * @format GUID
   * @readonly
   */
  businessLocationIds?: string[];
}

/** @oneof */
export interface ItemPricingOneOf {
  /** Item price variants. */
  priceVariants?: PriceVariants;
  /** Item price info. */
  priceInfo?: PriceInfo;
}

export interface PriceVariants {
  /**
   * List of price variants.
   * @maxSize 500
   */
  variants?: PriceVariant[];
}

export interface PriceVariant {
  /**
   * Price variant ID.
   * @format GUID
   */
  variantId?: string | null;
  /**
   * Price of a variant.
   * @decimalValue options { gte:0.00, maxScale:3 }
   * @deprecated Price of a variant.
   * @replacedBy pricing.price_variants.variants.price_info
   * @targetRemovalDate 2024-08-01
   */
  price?: string | null;
  /** Price info of a variant. */
  priceInfo?: PriceInfo;
}

export interface PriceInfo {
  /**
   * Price.
   * @decimalValue options { gte:0.00, maxScale:3 }
   */
  price?: string;
}

export interface Label {
  /**
   * Item label ID.
   * @format GUID
   */
  _id?: string;
}

export interface OrderSettings {
  /**
   * Whether the item is in stock.
   * Default: `true`.
   */
  inStock?: boolean | null;
  /**
   * Whether a customer can add a special request when ordering this item.
   * Default: `true`.
   */
  acceptSpecialRequests?: boolean | null;
}

export enum FulfillmentMethodType {
  /** Fulfillment method type is not specified. */
  FULFILLMENT_METHOD_TYPE_UNSPECIFIED = 'FULFILLMENT_METHOD_TYPE_UNSPECIFIED',
  /** Pickup fulfillment. */
  PICKUP = 'PICKUP',
  /** Delivery fulfillment. */
  DELIVERY = 'DELIVERY',
}

/** @enumType */
export type FulfillmentMethodTypeWithLiterals =
  | FulfillmentMethodType
  | 'FULFILLMENT_METHOD_TYPE_UNSPECIFIED'
  | 'PICKUP'
  | 'DELIVERY';

export interface Availability extends AvailabilityAvailabilityTypeOptionsOneOf {
  /** Settings for availability on a weekly schedule. */
  weeklyScheduleOptions?: WeeklyScheduleOptions;
  /** Settings for availability within a time range. */
  timestampRangesOptions?: TimestampRangesOptions;
  /** Availability type. */
  type?: AvailabilityTypeWithLiterals;
}

/** @oneof */
export interface AvailabilityAvailabilityTypeOptionsOneOf {
  /** Settings for availability on a weekly schedule. */
  weeklyScheduleOptions?: WeeklyScheduleOptions;
  /** Settings for availability within a time range. */
  timestampRangesOptions?: TimestampRangesOptions;
}

export enum AvailabilityType {
  /** Missing type due to an error. */
  UNSPECIFIED_AVAILABILITY_OPTION = 'UNSPECIFIED_AVAILABILITY_OPTION',
  /** Available all the time. */
  ALWAYS_AVAILABLE = 'ALWAYS_AVAILABLE',
  /** Available on specific days and times throughout the week. */
  WEEKLY_SCHEDULE = 'WEEKLY_SCHEDULE',
  /** Available during a specific time range. */
  TIMESTAMP_RANGES = 'TIMESTAMP_RANGES',
}

/** @enumType */
export type AvailabilityTypeWithLiterals =
  | AvailabilityType
  | 'UNSPECIFIED_AVAILABILITY_OPTION'
  | 'ALWAYS_AVAILABLE'
  | 'WEEKLY_SCHEDULE'
  | 'TIMESTAMP_RANGES';

export interface WeeklyScheduleOptions {
  /**
   * List of available time ranges for specific days of the week.
   * @maxSize 7
   */
  availableTimes?: DayOfWeekAvailability[];
}

export interface DayOfWeekAvailability {
  /** The day of week this availability relates to. */
  dayOfWeek?: EntitiesDayOfWeekWithLiterals;
  /** A list of time ranges during which the fulfillment should be available. */
  timeRanges?: TimeOfDayRange[];
}

export enum EntitiesDayOfWeek {
  /** Monday. */
  MON = 'MON',
  /** Tuesday. */
  TUE = 'TUE',
  /** Wednesday. */
  WED = 'WED',
  /** Thursday. */
  THU = 'THU',
  /** Friday. */
  FRI = 'FRI',
  /** Saturday. */
  SAT = 'SAT',
  /** Sunday. */
  SUN = 'SUN',
}

/** @enumType */
export type EntitiesDayOfWeekWithLiterals =
  | EntitiesDayOfWeek
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

export interface TimeOfDayRange {
  /** The start time in time of day representation. */
  startTime?: TimeOfDay;
  /** The end time in time of day representation. */
  endTime?: TimeOfDay;
}

export interface TimeOfDay {
  /**
   * Hours. <br />
   * Min: `0`. <br />
   * Max: `23`.
   */
  hours?: number;
  /**
   * Minutes. <br />
   * Min: `0`. <br />
   * Max: `23`.
   */
  minutes?: number;
}

export interface TimestampRangesOptions {
  /**
   * List of available time ranges.
   * @maxSize 50
   */
  ranges?: AvailableRange[];
}

export interface AvailableRange {
  /** The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  startTime?: Date | null;
  /** The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  endTime?: Date | null;
}

export interface ModifierGroup {
  /**
   * Modifier group ID.
   * @format GUID
   */
  _id?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ExternalReferenceInfo {
  /**
   * ID of the external integrator app\system associated with this object.
   * @format GUID
   */
  externalAppId?: string | null;
  /**
   * Reference to an external resource ID. Used to link this object to a specific entity in an external system.
   * @maxLength 100
   */
  externalEntityId?: string | null;
  /** Last time this item was synced with the external app. */
  lastSyncDate?: Date | null;
  /** Whether the connection to the external system is currently active. */
  connected?: boolean | null;
}

export interface DuplicateItemRequest {
  /**
   * Item id to be duplicated.
   * @format GUID
   */
  _id: string;
  /**
   * Business location ids to duplicate the item to.
   * @format GUID
   * @maxSize 100
   */
  businessLocationIds?: string[] | null;
}

export interface DuplicateItemResponse {
  /** @format GUID */
  itemId?: string[];
}

export interface UpdateDocumentsEvent
  extends UpdateDocumentsEventOperationOneOf {
  /** insert/update documents */
  update?: DocumentUpdateOperation;
  /** delete by document ids */
  deleteByIds?: DeleteByIdsOperation;
  /** delete documents matching filter */
  deleteByFilter?: DeleteByFilterOperation;
  /** update documents matching filter */
  updateByFilter?: UpdateByFilterOperation;
  /** update only existing documents */
  updateExisting?: UpdateExistingOperation;
  /**
   * application which owns documents
   * @minLength 2
   */
  appDefId?: string | null;
  /**
   * type of the documents
   * @minLength 2
   */
  documentType?: string | null;
  /**
   * language of the documents
   * @minLength 2
   */
  language?: string | null;
  /**
   * site documents belong to
   * @minLength 2
   */
  msId?: string | null;
}

/** @oneof */
export interface UpdateDocumentsEventOperationOneOf {
  /** insert/update documents */
  update?: DocumentUpdateOperation;
  /** delete by document ids */
  deleteByIds?: DeleteByIdsOperation;
  /** delete documents matching filter */
  deleteByFilter?: DeleteByFilterOperation;
  /** update documents matching filter */
  updateByFilter?: UpdateByFilterOperation;
  /** update only existing documents */
  updateExisting?: UpdateExistingOperation;
}

export interface DocumentUpdateOperation {
  /** documents to index or update */
  documents?: IndexDocument[];
}

export interface IndexDocument {
  /** data bag with non-searchable fields (url, image) */
  payload?: DocumentPayload;
  /** what type of users should documents be visible to */
  exposure?: EnumWithLiterals;
  /** document with mandatory fields (id, title, description) and with fields specific to the type of the document */
  document?: Record<string, any> | null;
  /** what member groups is the document exposed to. Used only with GROUP_PROTECTED exposure */
  permittedMemberGroups?: string[];
  /** if true SEO is disabled for this document */
  seoHidden?: boolean | null;
  /** if true the page is a lightbox popup */
  isPopup?: boolean | null;
}

export interface DocumentPayload {
  /** url of the page representing the document */
  url?: string | null;
  /** image which represents the document */
  documentImage?: DocumentImage;
}

export interface DocumentImage {
  /** the name of the image */
  name?: string;
  /** the width of the image */
  width?: number;
  /** the height of the image */
  height?: number;
}

export enum Enum {
  /** Default value. Means that permission not set */
  UNKNOWN = 'UNKNOWN',
  /** Protected exposure. Exposed to members and owners */
  PROTECTED = 'PROTECTED',
  /** Private exposure. Exposed to owners */
  PRIVATE = 'PRIVATE',
  /** Public exposure. Visible to everyone */
  PUBLIC = 'PUBLIC',
  /** Used for partial updates, to state that exposure is not changing */
  UNCHANGED = 'UNCHANGED',
  /** Protected to members of permitted groups and owners */
  GROUP_PROTECTED = 'GROUP_PROTECTED',
}

/** @enumType */
export type EnumWithLiterals =
  | Enum
  | 'UNKNOWN'
  | 'PROTECTED'
  | 'PRIVATE'
  | 'PUBLIC'
  | 'UNCHANGED'
  | 'GROUP_PROTECTED';

export interface DeleteByIdsOperation {
  /** ids of the documents to delete */
  documentIds?: string[];
}

export interface DeleteByFilterOperation {
  /** documents matching this filter wil be deleted. only filterable documents defined in document_type can be used for filtering */
  filter?: Record<string, any> | null;
}

export interface UpdateByFilterOperation {
  /** documents matching this filter will be updated */
  filter?: Record<string, any> | null;
  /** partial document to apply */
  document?: IndexDocument;
}

export interface UpdateExistingOperation {
  /** documents to update */
  documents?: IndexDocument[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface SearchIndexingNotification {
  /** new state of indexing for the site specified in ms_id */
  indexState?: StateWithLiterals;
  /** type of the document the notification is targeted for. Applies to all types if not provided */
  documentType?: string | null;
  /** languaInternalDocumentUpdateByFilterOperationge the notification is targeted for. Applies to all languages if not provided */
  language?: string | null;
  /**
   * site for which notification is targeted
   * @minLength 2
   */
  msId?: string | null;
}

export enum State {
  /** default state */
  Unknown = 'Unknown',
  /** metasite does not require site search indexing */
  Off = 'Off',
  /** metasite requires site search indexing */
  On = 'On',
}

/** @enumType */
export type StateWithLiterals = State | 'Unknown' | 'Off' | 'On';

/** Encapsulates all details written to the Greyhound topic when a site's properties are updated. */
export interface SitePropertiesNotification {
  /** The site ID for which this update notification applies. */
  metasiteId?: string;
  /** The actual update event. */
  event?: SitePropertiesEvent;
  /**
   * A convenience set of mappings from the MetaSite ID to its constituent services.
   * @maxSize 500
   */
  translations?: Translation[];
  /** Context of the notification */
  changeContext?: ChangeContext;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

/** A single mapping from the MetaSite ID to a particular service. */
export interface Translation {
  /** The service type. */
  serviceType?: string;
  /** The application definition ID; this only applies to services of type ThirdPartyApps. */
  appDefId?: string;
  /** The instance ID of the service. */
  instanceId?: string;
}

export interface ChangeContext extends ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

/** @oneof */
export interface ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

export interface PropertiesChange {}

export interface SiteCreated {
  /** Origin template site id. */
  originTemplateId?: string | null;
}

export interface SiteCloned {
  /** Origin site id. */
  originMetaSiteId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface DeleteLocationIdsFromVariants {
  /**
   * @format GUID
   * @maxSize 100
   */
  businessLocationIds?: string[];
  /**
   * @format GUID
   * @maxSize 1000
   */
  variantIds?: string[];
}

export interface DeleteLocationIdsFromModifierGroups {
  /**
   * @format GUID
   * @maxSize 100
   */
  businessLocationIds?: string[];
  /**
   * @format GUID
   * @maxSize 1000
   */
  modifierGroupIds?: string[];
}

export interface CreateItemRequest {
  /** Item details. */
  item: Item;
}

export interface CreateItemResponse {
  /** Item. */
  item?: Item;
}

export interface BulkCreateItemsRequest {
  /**
   * Items details.
   * @maxSize 100
   */
  items: Item[];
  /** Whether to return entity in the response. */
  returnEntity?: boolean;
}

export interface BulkCreateItemsResponse {
  /** Information about the created items. */
  results?: BulkCreateItemResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
  /** Created item. */
  item?: Item;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetItemRequest {
  /**
   * Item ID.
   * @format GUID
   */
  itemId: string;
}

export interface GetItemResponse {
  /** Item. */
  item?: Item;
}

export interface ListItemsRequest {
  /** Item IDs. */
  itemIds?: string[];
  /** The metadata of the paginated results. */
  paging?: CursorPaging;
  /** Whether to return only items that are visible to site visitors. */
  onlyVisible?: boolean | null;
}

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 500
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface ListItemsResponse {
  /** Items. */
  items?: Item[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface QueryItemsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryItemsResponse {
  /** Retrieved items. */
  items?: Item[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface SearchItemsRequest {
  /** Search options. */
  search?: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about cursor paging ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#cursor-paging) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging)).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about filtering ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#the-filter-object) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters)).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about sorting ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#the-sort-array) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting)).
   */
  sort?: Sorting[];
  /** Logical groupings of data into facets, with summaries for each facet. For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories. */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about cursor paging ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#cursor-paging) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging)).
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** Aggregation name, returned in `aggregations.results.name`. */
  name?: string | null;
  /** Type of aggregation to perform. Must align with the corresponding aggregation field. */
  type?: AggregationTypeWithLiterals;
  /** Field to aggreg   ate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`. */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` is not provided. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` is not provided. */
  to?: number | null;
}

export enum SortType {
  /** Number of matches in the results. */
  COUNT = 'COUNT',
  /** Alphabetically by the field value. */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Descending order. */
  DESC = 'DESC',
  /** Ascending order. */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum ScalarType {
  UNKNOWN_SCALAR_TYPE = 'UNKNOWN_SCALAR_TYPE',
  /** Total number of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'UNKNOWN_SCALAR_TYPE'
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export enum AggregationType {
  UNKNOWN_AGGREGATION_TYPE = 'UNKNOWN_AGGREGATION_TYPE',
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'UNKNOWN_AGGREGATION_TYPE'
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR';

export interface ValueAggregation {
  /** Sort type. */
  sortType?: SortTypeWithLiterals;
  /** Sort direction. */
  sortDirection?: SortDirectionWithLiterals;
  /**
   * Number of aggregation results to return.
   * Min: `1`
   * Max: `250`
   * Default: `10`
   */
  limit?: number | null;
}

export interface RangeAggregation {
  /** List of range buckets. During aggregation each entity is placed in the first bucket its value falls into, based on the provided range bounds. */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface SearchDetails {
  /** Search mode. Defines the search logic for combining multiple terms in the `expression`. */
  mode?: ModeWithLiterals;
  /** Search term or expression. */
  expression?: string | null;
  /** Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`. */
  fields?: string[];
  /** Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions. */
  fuzzy?: boolean;
}

export enum Mode {
  /** At least one of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface SearchItemsResponse {
  /** Retrieved items. */
  items?: Item[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
  /** The metadata of the search results. */
  aggregationData?: AggregationData;
}

export interface AggregationData {
  /** List of the aggregated data results. */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /** Value of the field. */
  value?: string;
  /** Number of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Total number of entities in this range. */
  count?: number;
}

export interface ValueResults {
  /** List of value aggregations. */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /** List of ranges returned in the same order as requested. */
  results?: RangeAggregationResult[];
}

export interface ScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: ScalarResult;
  /** Aggregation name, returned in `aggregations.results.name`. */
  name?: string;
  /** Type of aggregation to perform. Must align with the corresponding aggregation field. */
  type?: AggregationTypeWithLiterals;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: ScalarResult;
}

export interface CountItemsRequest {
  /** Filter for counting items. */
  filter?: Record<string, any> | null;
  /** Search details. */
  searchDetails?: SearchDetails;
}

export interface CountItemsResponse {
  /** Counted items. */
  count?: number;
}

export interface UpdateItemRequest {
  /** Item to update. */
  item: Item;
}

export interface UpdateItemResponse {
  /** Updated item. */
  item?: Item;
}

export interface BulkUpdateItemRequest {
  /**
   * Items to update.
   * @minSize 1
   * @maxSize 100
   */
  items: MaskedItem[];
  /** Whether to return entity in the response. */
  returnEntity?: boolean;
}

export interface MaskedItem {
  /** Item to update. */
  item?: Item;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateItemResponse {
  /**
   * Information about the updated items.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateItemResponseBulkItemResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateItemResponseBulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
  /** Updated item. Only returned if `returnEntity` is set to `true`. */
  item?: Item;
}

export interface DeleteItemRequest {
  /**
   * Item ID.
   * @format GUID
   */
  itemId: string;
}

export interface DeleteItemResponse {}

export interface BulkDeleteItemsRequest {
  /**
   * Item IDs.
   * @format GUID
   */
  ids: string[];
}

export interface BulkDeleteItemsResponse {
  /** Information about the deleted items. */
  results?: BulkDeleteItemResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteItemResult {
  /** Metadata for item delete. */
  itemMetadata?: ItemMetadata;
}

export interface CloneItemsRequest {
  /**
   * The MetaSiteId to clone from.
   * @format GUID
   */
  metaSiteId?: string;
}

export interface CloneItemsResponse {}

export interface BulkUpdateBusinessLocationIdsRequest {
  /**
   * item ids
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  itemIds?: string[];
  /**
   * Business Location Ids.
   * @maxSize 100
   * @format GUID
   */
  businessLocationIds?: string[];
}

export interface BulkUpdateBusinessLocationIdsResponse {
  /**
   * Information about the updated items.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBusinessLocationIdsResponseBulkItemResult[];
}

export interface BulkUpdateBusinessLocationIdsResponseBulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateBusinessLocationIdsPerEntityRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  itemIdLocationIds?: ItemIdLocationIds[];
}

export interface ItemIdLocationIds {
  /**
   * item ID
   * @format GUID
   */
  itemId?: string;
  /**
   * Business Location IDs
   * @maxSize 100
   * @format GUID
   */
  businessLocationIds?: string[];
}

export interface BulkUpdateBusinessLocationIdsPerEntityResponse {
  /**
   * Information about the updated items.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkItemResult[];
}

export interface BulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface SyncContentToMultilingualRequest {
  /**
   * MetaSite ID
   * @format GUID
   */
  metaSiteId?: string;
}

export interface SyncContentToMultilingualResponse {}

/** @docsIgnore */
export type CountItemsApplicationErrors = {
  code?: 'TOO_MANY_TO_COUNT';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ItemCreatedEnvelope {
  entity: Item;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.ITEM_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_created
 * @serviceIdentifier com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem
 * @slug created
 */
export declare function onItemCreated(
  handler: (event: ItemCreatedEnvelope) => void | Promise<void>
): void;

export interface ItemDeletedEnvelope {
  entity: Item;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.ITEM_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_deleted
 * @serviceIdentifier com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem
 * @slug deleted
 */
export declare function onItemDeleted(
  handler: (event: ItemDeletedEnvelope) => void | Promise<void>
): void;

export interface ItemUpdatedEnvelope {
  entity: Item;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when an item is updated.
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.ITEM_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_updated
 * @serviceIdentifier com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem
 * @slug updated
 */
export declare function onItemUpdated(
  handler: (event: ItemUpdatedEnvelope) => void | Promise<void>
): void;

/** @param _id - Item id to be duplicated.
 * @public
 * @requiredField _id
 * @permissionId RESTAURANTS.ITEM_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.restaurants_duplicate_item.RestaurantsDuplicateItem.DuplicateItem
 */
export async function duplicateItem(
  _id: string,
  options?: DuplicateItemOptions
): Promise<NonNullablePaths<DuplicateItemResponse, `itemId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    businessLocationIds: options?.businessLocationIds,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.duplicateItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          businessLocationIds: '$[1].businessLocationIds',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DuplicateItemOptions {
  /**
   * Business location ids to duplicate the item to.
   * @format GUID
   * @maxSize 100
   */
  businessLocationIds?: string[] | null;
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates an item.
 *
 * To create multiple items at once, use [Bulk Create Items](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item/bulk-create-items).
 * @param item - Item details.
 * @public
 * @requiredField item
 * @requiredField item.modifierGroups._id
 * @permissionId RESTAURANTS.ITEM_CREATE
 * @applicableIdentity APP
 * @returns Item.
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.CreateItem
 */
export async function createItem(
  item: NonNullablePaths<Item, `modifierGroups.${number}._id`, 4>
): Promise<
  NonNullablePaths<
    Item,
    | `priceVariants.variants`
    | `priceVariants.variants.${number}.priceInfo.price`
    | `name`
    | `additionalImages`
    | `labels`
    | `labels.${number}._id`
    | `modifierGroups`
    | `businessLocationIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ item: item }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'item.image' },
          { path: 'item.additionalImages', isRepeated: true },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.createItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'item.image' },
            { path: 'item.additionalImages', isRepeated: true },
          ],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { item: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['item']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Items API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates multiple items at once.
 * @param items - Items details.
 * @public
 * @requiredField items
 * @requiredField items.modifierGroups._id
 * @permissionId RESTAURANTS.ITEM_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.BulkCreateItems
 */
export async function bulkCreateItems(
  items: NonNullablePaths<Item, `modifierGroups.${number}._id`, 4>[],
  options?: BulkCreateItemsOptions
): Promise<
  NonNullablePaths<
    BulkCreateItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.priceInfo.price`
    | `results.${number}.item.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      items: items,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'items.image' },
          { path: 'items.additionalImages', isRepeated: true },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.bulkCreateItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.image' },
            { path: 'results.item.additionalImages', isRepeated: true },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          items: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['items', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateItemsOptions {
  /** Whether to return entity in the response. */
  returnEntity?: boolean;
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves an item by ID.
 * @param itemId - Item ID.
 * @public
 * @requiredField itemId
 * @permissionId RESTAURANTS.ITEM_READ
 * @applicableIdentity APP
 * @returns Item.
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.GetItem
 */
export async function getItem(
  itemId: string
): Promise<
  NonNullablePaths<
    Item,
    | `priceVariants.variants`
    | `priceVariants.variants.${number}.priceInfo.price`
    | `name`
    | `additionalImages`
    | `labels`
    | `labels.${number}._id`
    | `modifierGroups`
    | `businessLocationIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.getItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'item.image' },
            { path: 'item.additionalImages', isRepeated: true },
          ],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of up to 500 items.
 * @public
 * @permissionId RESTAURANTS.ITEM_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.ListItems
 */
export async function listItems(
  options?: ListItemsOptions
): Promise<
  NonNullablePaths<
    ListItemsResponse,
    `items` | `items.${number}.priceInfo.price` | `items.${number}.name`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    itemIds: options?.itemIds,
    paging: options?.paging,
    onlyVisible: options?.onlyVisible,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.listItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'items.image' },
            { path: 'items.additionalImages', isRepeated: true },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          itemIds: '$[0].itemIds',
          paging: '$[0].paging',
          onlyVisible: '$[0].onlyVisible',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListItemsOptions {
  /** Item IDs. */
  itemIds?: string[];
  /** The metadata of the paginated results. */
  paging?: CursorPaging;
  /** Whether to return only items that are visible to site visitors. */
  onlyVisible?: boolean | null;
}

/**
 * Creates a query to retrieve a list of items.
 *
 * The `queryItems()` function builds a query to retrieve a list of items and returns a `ItemsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/items/items-query-builder/find) function.
 *
 * You can refine the query by chaining `ItemsQueryBuilder` functions onto the query. `ItemsQueryBuilder` functions enable you to filter, sort, and control the results that `queryItems()` returns.
 *
 * `queryItems()` runs with the following `ItemsQueryBuilder` defaults, which you can override:
 *
 * * [`limit(500)`](/items/items-query-builder/limit)
 *
 * The following `ItemsQueryBuilder` functions are supported for `queryItems()`. For a full description of the item object, see the object returned for the [`items`](/items/items-query-result/items) property in `ItemsQueryResult`.
 * @public
 * @permissionId RESTAURANTS.ITEM_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.QueryItems
 */
export function queryItems(): ItemsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Item, 'CURSOR', QueryItemsRequest, QueryItemsResponse>({
    func: async (payload: QueryItemsRequest) => {
      const reqOpts = ambassadorWixRestaurantsMenusV1Item.queryItems(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryItemsRequest['query']) => {
      const args = [query, {}] as [QueryItemsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryItemsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'items.image' },
              { path: 'items.additionalImages', isRepeated: true },
            ],
          },
        ])
      );

      return {
        items: transformedData?.items,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ItemsQueryResult extends QueryCursorResult {
  items: Item[];
  query: ItemsQueryBuilder;
  next: () => Promise<ItemsQueryResult>;
  prev: () => Promise<ItemsQueryResult>;
}

export interface ItemsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | 'priceVariants.variants.price'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'visible'
      | 'featured'
      | 'orderSettings.inStock'
      | 'orderSettings.acceptSpecialRequests'
      | 'modifierGroups.id'
      | 'businessLocationIds'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId'
      | 'externalReferenceInfo.connected',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | 'priceVariants.variants.price'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'visible'
      | 'featured'
      | 'orderSettings.inStock'
      | 'orderSettings.acceptSpecialRequests'
      | 'modifierGroups.id'
      | 'businessLocationIds'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId'
      | 'externalReferenceInfo.connected',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'modifierGroups.id'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'modifierGroups.id'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'modifierGroups.id'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'modifierGroups.id'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | '_id'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'modifierGroups.id'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId',
    value: string
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'visible'
      | 'featured'
      | 'modifierGroups.id'
      | 'businessLocationIds'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId'
      | 'externalReferenceInfo.connected',
    value: any[]
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (
    propertyName: 'businessLocationIds',
    value: any[]
  ) => ItemsQueryBuilder;
  in: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | 'priceVariants.variants.price'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'visible'
      | 'featured'
      | 'orderSettings.inStock'
      | 'orderSettings.acceptSpecialRequests'
      | 'modifierGroups.id'
      | 'businessLocationIds'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId'
      | 'externalReferenceInfo.connected',
    value: any
  ) => ItemsQueryBuilder;
  exists: (
    propertyName:
      | 'priceVariants.variants.variantId'
      | 'priceVariants.variants.price'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'labels.id'
      | 'visible'
      | 'featured'
      | 'orderSettings.inStock'
      | 'orderSettings.acceptSpecialRequests'
      | 'modifierGroups.id'
      | 'businessLocationIds'
      | 'externalReferenceInfo.externalAppId'
      | 'externalReferenceInfo.externalEntityId'
      | 'externalReferenceInfo.connected',
    value: boolean
  ) => ItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | 'priceVariants.variants.price'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'visible'
      | 'featured'
      | 'orderSettings.inStock'
      | 'orderSettings.acceptSpecialRequests'
      | 'orderSettings.taxGroupId'
      | 'oloMigrationItemId'
      | 'businessLocationIds'
    >
  ) => ItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | 'priceVariants.variants.price'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'image.id'
      | 'visible'
      | 'featured'
      | 'orderSettings.inStock'
      | 'orderSettings.acceptSpecialRequests'
      | 'orderSettings.taxGroupId'
      | 'oloMigrationItemId'
      | 'businessLocationIds'
    >
  ) => ItemsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ItemsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => ItemsQueryBuilder;
  find: () => Promise<ItemsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.QueryItems
 * @requiredField query
 */
export async function typedQueryItems(
  query: ItemQuery
): Promise<
  NonNullablePaths<
    QueryItemsResponse,
    `items` | `items.${number}.priceInfo.price` | `items.${number}.name`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.queryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'items.image' },
            { path: 'items.additionalImages', isRepeated: true },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ItemQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['businessLocationIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'description',
        'featured',
        'image._id',
        'name',
        'visible'
      ];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: [
        'orderSettings.acceptSpecialRequests',
        'orderSettings.inStock',
        'priceVariants.variants.price'
      ];
      operators: ['$eq', '$exists', '$in', '$ne', '$nin'];
      sort: 'BOTH';
    },
    {
      fields: [
        'labels._id',
        'modifierGroups._id',
        'priceVariants.variants.variantId'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: [
        'externalReferenceInfo.connected',
        'externalReferenceInfo.externalAppId',
        'externalReferenceInfo.externalEntityId'
      ];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Item, ItemQuerySpec>;
export type ItemQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 500 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Item, ItemQuerySpec, ItemQuery>(),
  },
  search: {
    ...createSearchUtils<Item, ItemSearchSpec, ItemSearch>(),
  },
};

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of items given the provided filtering, paging, and sorting.
 *
 * ## Defaults
 * Search Items has the following default settings, which you can override:
 *
 * - Sorted by `createdDate` in ascending order.
 * - `paging.limit` set to `500`.
 * - `paging.offset` set to `0`.
 * @param search - Search options.
 * @public
 * @requiredField search
 * @permissionId RESTAURANTS.ITEM_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.SearchItems
 */
export async function searchItems(
  search: ItemSearch
): Promise<
  NonNullablePaths<
    SearchItemsResponse,
    | `items`
    | `items.${number}.priceInfo.price`
    | `items.${number}.name`
    | `aggregationData.results`
    | `aggregationData.results.${number}.scalar.type`
    | `aggregationData.results.${number}.scalar.value`
    | `aggregationData.results.${number}.name`
    | `aggregationData.results.${number}.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ search: search });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.searchItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'items.image' },
            { path: 'items.additionalImages', isRepeated: true },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { search: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['search']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ItemSearchSpec extends SearchSpec {
  searchable: ['name'];
  aggregatable: [
    '_createdDate',
    '_id',
    '_updatedDate',
    'businessLocationIds',
    'featured',
    'image._id',
    'labels._id',
    'modifierGroups._id',
    'name',
    'orderSettings.inStock',
    'visible'
  ];
  paging: 'cursor';
  wql: [
    {
      operators: ['$hasAll', '$hasSome'];
      fields: ['businessLocationIds', 'labels._id', 'modifierGroups._id'];
      sort: 'BOTH';
    },
    {
      operators: '*';
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'featured',
        'image._id',
        'name',
        'orderSettings.inStock',
        'visible'
      ];
      sort: 'BOTH';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<Item, ItemSearchSpec>;
export type ItemSearch = {
  /** 
  Cursor paging options.

  Learn more about cursor paging ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#cursor-paging) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging)).  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 500 
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about filtering ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#the-filter-object) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters)).  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about sorting ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#the-sort-array) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting)).  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Logical groupings of data into facets, with summaries for each facet. For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories.  
  */
  aggregations?: {
    /** 
  A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.  
  */
    value?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['value'];
    /** 
  A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.  
  */
    range?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['range'];
    /** 
  A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.  
  */
    scalar?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['scalar'];
    /** 
  Aggregation name, returned in `aggregations.results.name`.  
  */
    name?:
      | NonNullable<
          CommonSearchWithEntityContext['aggregations']
        >[number]['name']
      | null;
    /** 
  Type of aggregation to perform. Must align with the corresponding aggregation field.  
  */
    type?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['type'];
    /** 
  Field to aggreg   ate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.  
  */
    fieldPath?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['fieldPath'];
  }[];
  /** 
  Free text to match in searchable fields.  
  */
  search?: {
    /** 
  Search mode. Defines the search logic for combining multiple terms in the `expression`.  
  */
    mode?: NonNullable<CommonSearchWithEntityContext['search']>['mode'];
    /** 
  Search term or expression.  
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`.  
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
};

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves the number of items that match a specified filter.
 *
 * If a filter isn't passed in the request, the endpoint returns the count of all items.
 * @public
 * @permissionId RESTAURANTS.ITEM_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.CountItems
 */
export async function countItems(options?: CountItemsOptions): Promise<
  NonNullablePaths<CountItemsResponse, `count`, 2> & {
    __applicationErrorsType?: CountItemsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
    searchDetails: options?.searchDetails,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.countItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0].filter',
          searchDetails: '$[0].searchDetails',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountItemsOptions {
  /** Filter for counting items. */
  filter?: Record<string, any> | null;
  /** Search details. */
  searchDetails?: SearchDetails;
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates an item.
 *
 * To update multiple items at once, use [Bulk Update Item](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item/bulk-update-item)
 *
 * Each time an item is updated, its revision increments by 1. The existing revision must be included when updating the item. This ensures you're working with the latest item information, and it prevents unintended overwrites.
 * @param _id - Item ID.
 * @public
 * @requiredField _id
 * @requiredField item
 * @requiredField item.modifierGroups._id
 * @requiredField item.revision
 * @permissionId RESTAURANTS.ITEM_UPDATE
 * @applicableIdentity APP
 * @returns Updated item.
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.UpdateItem
 */
export async function updateItem(
  _id: string,
  item: NonNullablePaths<
    UpdateItem,
    `modifierGroups.${number}._id` | `revision`,
    4
  >
): Promise<
  NonNullablePaths<
    Item,
    | `priceVariants.variants`
    | `priceVariants.variants.${number}.priceInfo.price`
    | `name`
    | `additionalImages`
    | `labels`
    | `labels.${number}._id`
    | `modifierGroups`
    | `businessLocationIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ item: { ...item, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'item.image' },
          { path: 'item.additionalImages', isRepeated: true },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.updateItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'item.image' },
            { path: 'item.additionalImages', isRepeated: true },
          ],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { item: '$[1]' },
        explicitPathsToArguments: { 'item.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'item']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateItem {
  /** Item price variants. */
  priceVariants?: PriceVariants;
  /** Item price info. */
  priceInfo?: PriceInfo;
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the item was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Item name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  /**
   * Item description.
   * @maxLength 1500
   */
  description?: string | null;
  /** Main item image. */
  image?: string;
  /**
   * Additional item images.
   * @maxSize 15
   */
  additionalImages?: string[];
  /**
   * Item labels.
   * @maxSize 10
   */
  labels?: Label[];
  /** Whether the item is visible in the menu for site visitors. */
  visible?: boolean | null;
  /**
   * Whether the item is marked as featured/best seller.
   * Default: `false`.
   */
  featured?: boolean | null;
  /** Online order settings. */
  orderSettings?: OrderSettings;
  /**
   * Item modifier groups.
   * @maxSize 25
   */
  modifierGroups?: ModifierGroup[];
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * IDs of the business locations ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where these items are available.
   * @maxSize 100
   * @format GUID
   * @readonly
   */
  businessLocationIds?: string[];
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates up to 100 multiple items at once.
 *
 * Each time an item is updated, its revision increments by 1. The existing revision must be included when updating item. This ensures you're working with the latest item information, and it prevents unintended overwrites.
 * @param items - Items to update.
 * @public
 * @requiredField items
 * @requiredField items.item._id
 * @requiredField items.item.modifierGroups._id
 * @requiredField items.item.revision
 * @permissionId RESTAURANTS.ITEM_UPDATE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.BulkUpdateItem
 */
export async function bulkUpdateItem(
  items: NonNullablePaths<
    MaskedItem,
    `item._id` | `item.modifierGroups.${number}._id` | `item.revision`,
    5
  >[],
  options?: BulkUpdateItemOptions
): Promise<
  NonNullablePaths<
    BulkUpdateItemResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.priceInfo.price`
    | `results.${number}.item.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      items: items,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'items.item.image' },
          { path: 'items.item.additionalImages', isRepeated: true },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.bulkUpdateItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.image' },
            { path: 'results.item.additionalImages', isRepeated: true },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          items: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['items', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateItemOptions {
  /** Whether to return entity in the response. */
  returnEntity?: boolean;
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes an item.
 * @param itemId - Item ID.
 * @public
 * @requiredField itemId
 * @permissionId RESTAURANTS.ITEM_DELETE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.DeleteItem
 */
export async function deleteItem(itemId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.deleteItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes multiple items at once.
 * @param ids - Item IDs.
 * @public
 * @requiredField ids
 * @permissionId RESTAURANTS.ITEM_DELETE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item.v1.RestaurantsMenusItem.BulkDeleteItems
 */
export async function bulkDeleteItems(
  ids: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts = ambassadorWixRestaurantsMenusV1Item.bulkDeleteItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
